import { Controller } from "@hotwired/stimulus"
import { createLogger } from "~/utils/logger"

const log = createLogger("CommandPalette")

/**
 * Command Palette Controller
 *
 * Manages the command palette modal functionality including:
 * - Global keyboard shortcuts (Cmd/Ctrl+K to toggle)
 * - Access key navigation (Shift+1..9 to select results)
 * - Focus management (auto-focus search input on open)
 * - Navigation handling (close on result click)
 * - ESC key handling (native dialog behavior)
 *
 * Keyboard Shortcuts:
 * - Cmd/Ctrl+K: Toggle modal visibility
 * - ESC: Close modal (native <dialog> behavior)
 * - Shift+1..9: Navigate to result by access key
 *
 * Usage:
 *   <dialog data-controller="command-palette"
 *           data-action="keydown.meta+k@window->command-palette#toggle
 *                        keydown.ctrl+k@window->command-palette#toggle">
 *     <!-- Modal content -->
 *   </dialog>
 *
 * Targets:
 * - searchInput: The search input field (auto-focused on open)
 * - resultsContainer: Container for search results
 * - resultItem: Individual result items (for access key navigation)
 *
 * Actions:
 * - toggle(): Toggle modal open/closed
 * - open(): Open the modal
 * - close(): Close the modal
 * - navigate(): Handle navigation when result is clicked
 * - handleAccessKey(event): Navigate to result by access key (Shift+1..9)
 */
export default class extends Controller {
  // Stimulus targets
  static targets = ["searchInput", "resultsContainer", "resultItem", "footer", "container"]

  // Stimulus values for state management
  static values = {
    mode: { type: String, default: "pages" }, // "pages", "actions", or "ai"
    animationDuration: { type: Number, default: 100 } // Exit animation duration in ms (matches CSS)
  }

  /**
   * Callback when mode value changes
   * Updates the footer to show context-appropriate shortcuts
   */
  modeValueChanged() {
    this.updateFooter()
  }

  /**
   * Initialize controller and set up event listeners
   * Called when the controller is connected to the DOM
   */
  connect() {
    // Store bound event handlers for cleanup
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundHandleAccessKey = this.handleAccessKey.bind(this)
    this.boundHandleBeforeVisit = this.handleBeforeVisit.bind(this)
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    this.boundHandleArrowNavigation = this.handleArrowNavigation.bind(this)
    this.boundHandleCancel = this.handleCancel.bind(this)

    // Set up global keydown listener for access keys (Cmd/Ctrl+1..9)
    // We use window-level listener to capture shortcuts anywhere on page
    window.addEventListener("keydown", this.boundHandleAccessKey)

    // Set up keydown listener on the dialog itself for Cmd+Enter, ESC, and arrow keys
    this.element.addEventListener("keydown", this.boundHandleGlobalKeydown)
    this.element.addEventListener("keydown", this.boundHandleArrowNavigation)

    // Close modal when Turbo navigation begins
    // Listen at document level to catch all Turbo navigations
    document.addEventListener("turbo:before-visit", this.boundHandleBeforeVisit)

    // Intercept native dialog cancel event (ESC key) to play exit animation
    this.element.addEventListener("cancel", this.boundHandleCancel)

    // Listen to native dialog close event to sync isOpen state
    // This handles ESC key press and other ways the dialog can be closed
    this.element.addEventListener("close", () => {
      this.isOpen = false
      this.selectedIndex = -1
      this.clearSearchInput()
      // Remove closing class in case it wasn't removed
      this.element.classList.remove('closing')
    })

    // Ensure dialog is closed on initial load
    // Native <dialog> elements can sometimes render in an open state
    // depending on CSS specificity and timing, so we explicitly close it
    if (this.element.open) {
      this.element.close()
    }
    this.isOpen = false
    this.selectedIndex = -1  // Track currently selected result (-1 = none)
    this.isClosing = false   // Track if we're in the middle of closing animation

    // Set up ResizeObserver to handle smooth height transitions
    this.setupHeightTransitions()
  }

  /**
   * Set up smooth height transitions using MutationObserver
   * Clean, simple approach: explicit height with smooth transitions
   */
  setupHeightTransitions() {
    if (!this.hasContainerTarget) return

    const container = this.containerTarget
    const turboFrame = container.querySelector('turbo-frame#command-palette-results')
    if (!turboFrame) return

    // Set initial explicit height after first render
    requestAnimationFrame(() => {
      if (this.isOpen) {
        const initialHeight = container.offsetHeight
        container.style.height = `${initialHeight}px`
      }
    })

    // Observe content changes
    this.mutationObserver = new MutationObserver(() => {
      if (!this.isOpen) return

      // Get current explicit height (our starting point)
      const oldHeight = parseInt(container.style.height) || container.offsetHeight

      // Measure new content size and animate
      requestAnimationFrame(() => {
        // Get the full scrollHeight of the container itself (includes all children)
        const newHeight = container.scrollHeight

        // Only animate if significantly different
        if (Math.abs(newHeight - oldHeight) > 5) {
          // Ensure transition is set for smooth animation
          container.style.transition = 'height 250ms ease-in-out'

          // Smooth height transition
          container.style.height = `${newHeight}px`
        }
      })
    })

    // Watch for DOM changes
    this.mutationObserver.observe(turboFrame, {
      childList: true,
      subtree: true,
      characterData: false
    })
  }

  /**
   * Cleanup event listeners when controller is disconnected
   * Prevents memory leaks and duplicate event handlers
   */
  disconnect() {
    window.removeEventListener("keydown", this.boundHandleAccessKey)
    this.element.removeEventListener("keydown", this.boundHandleGlobalKeydown)
    this.element.removeEventListener("keydown", this.boundHandleArrowNavigation)
    this.element.removeEventListener("cancel", this.boundHandleCancel)
    document.removeEventListener("turbo:before-visit", this.boundHandleBeforeVisit)

    // Cleanup observers
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    // Close dialog if open (skip animation on disconnect)
    if (this.isOpen) {
      this.element.close()
      this.isOpen = false
    }
  }

  /**
   * Toggle modal visibility
   * Called by Cmd/Ctrl+K keyboard shortcut
   *
   * @param {KeyboardEvent} event - The keyboard event (if triggered by shortcut)
   */
  toggle(event) {
    if (event) {
      event.preventDefault()
    }

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Open the command palette modal
   * - Shows the native <dialog> element
   * - Focuses the search input
   * - Updates internal state
   *
   * @param {Event} event - Optional event object
   */
  open(event) {
    if (event) {
      event.preventDefault()
    }

    // Prevent opening if already open
    if (this.isOpen) {
      return
    }

    try {
      // Reset to pages mode when opening
      this.modeValue = "pages"

      // Use native dialog showModal() method
      // This provides built-in features:
      // - Backdrop overlay
      // - ESC key handling
      // - Focus trapping
      // - Top-layer rendering (above other content)
      this.element.showModal()

      // Update state
      this.isOpen = true

      // Focus the search input after a small delay
      // Delay ensures the dialog is fully rendered and visible
      requestAnimationFrame(() => {
        this.focusSearchInput()

        // Reset container height to auto when opening
        if (this.hasContainerTarget) {
          this.containerTarget.style.height = 'auto'
        }
      })
    } catch (error) {
      log.error("Failed to open Command Palette:", error)
    }
  }

  /**
   * Handle native dialog cancel event (triggered by ESC key)
   * Prevents immediate close to allow exit animation to play
   *
   * @param {Event} event - The cancel event
   */
  handleCancel(event) {
    // Prevent default cancel behavior (immediate close)
    event.preventDefault()

    // Trigger our animated close instead
    this.close()
  }

  /**
   * Close the command palette modal
   * - Triggers exit animation
   * - Hides the dialog after animation completes
   * - Clears search input
   * - Updates internal state
   *
   * @param {Event} event - Optional event object
   */
  close(event) {
    if (event) {
      event.preventDefault()
    }

    // Prevent closing if already closed or currently closing
    if (!this.isOpen || this.isClosing) {
      return
    }

    try {
      // Mark as closing to prevent duplicate close calls
      this.isClosing = true
      this.isOpen = false

      // Add closing class to trigger exit animation
      this.element.classList.add('closing')

      // Wait for exit animation to complete before actually closing
      setTimeout(() => {
        // Use native dialog close() method
        this.element.close()

        // Remove closing state
        this.element.classList.remove('closing')
        this.isClosing = false

        // Clear search input on close
        this.clearSearchInput()
      }, this.animationDurationValue) // Match exit animation duration from Stimulus value
    } catch (error) {
      log.error("Failed to close Command Palette:", error)
      this.isClosing = false
    }
  }

  /**
   * Handle Turbo navigation start event
   * Close the modal when user navigates to a search result
   *
   * @param {Event} event - The turbo:before-visit event
   */
  handleBeforeVisit(event) {
    // Only close if the modal is open and the navigation originated from within the modal
    if (this.isOpen && this.element.contains(event.target)) {
      this.close()
    }
  }

  /**
   * @deprecated - No longer used, kept for reference
   * Handle navigation when a result is clicked
   * Now handled by Turbo with data-turbo-frame="_top"
   *
   * @param {MouseEvent} event - The click event
   */
  navigate(event) {
    // This method is deprecated
    // Navigation is now handled by Turbo's native link behavior
    this.close()
  }

  /**
   * Handle access key shortcuts (Cmd+1..9 on Mac, Ctrl+1..9 on Windows/Linux)
   * Allows keyboard navigation to results without mouse
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleAccessKey(event) {
    // Only handle if:
    // 1. Modal is open
    // 2. Cmd/Ctrl key is pressed (NOT Shift, to avoid special characters)
    // 3. A number key (1-9) is pressed
    if (!this.isOpen) {
      return
    }

    // Check for Cmd (Mac) or Ctrl (Windows/Linux)
    if (!event.metaKey && !event.ctrlKey) {
      return
    }

    // Check if it's a number key (1-9)
    const key = event.key
    const keyNumber = parseInt(key, 10)

    if (isNaN(keyNumber) || keyNumber < 1 || keyNumber > 9) {
      return
    }

    // Prevent default browser behavior (e.g., browser shortcuts)
    event.preventDefault()

    // Find the result item with matching access key
    const targetItem = this.resultItemTargets.find(item => {
      const accessKey = item.dataset.accessKey
      return accessKey && parseInt(accessKey, 10) === keyNumber
    })

    if (targetItem) {
      // Trigger click on the result item
      // This will navigate to the result's URL and close the modal
      targetItem.click()
    }
  }

  /**
   * Handle global keydown events on the dialog for mode switching
   * - Cmd/Ctrl+Enter: Switch to Quick Actions mode
   * - Cmd/Ctrl+I: Show AI coming soon message
   * - ESC: Go back to Pages mode (or close if already in Pages mode)
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleGlobalKeydown(event) {
    // Handle Cmd/Ctrl+Enter to switch to Quick Actions mode
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.switchToActionsMode()
      return
    }

    // Handle Cmd/Ctrl+I for AI assistant mode
    if (event.key === "i" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.switchToAiMode()
      return
    }

    // Handle ESC key
    if (event.key === "Escape") {
      if (this.modeValue === "actions" || this.modeValue === "ai") {
        // If in actions or ai mode, go back to pages mode
        event.preventDefault()
        this.switchToPagesMode()
      } else {
        // If in pages mode, let native dialog handle closing
        // (dialog will close automatically)
      }
    }
  }

  /**
   * Handle keydown events on the dialog
   * Currently unused as ESC is handled natively by <dialog>
   * Kept for potential future keyboard shortcuts
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    // ESC key is handled natively by <dialog> element
    // Add other keyboard shortcuts here if needed
  }

  /**
   * Switch to Quick Actions mode
   * Shows only Quick Actions, hides Pages and other resources
   */
  switchToActionsMode() {
    this.modeValue = "actions"
    this.updatePlaceholder()
    this.refreshResults()
  }

  /**
   * Switch back to Pages mode (default)
   * Shows Pages and search results, hides Quick Actions
   */
  switchToPagesMode() {
    this.modeValue = "pages"
    this.updatePlaceholder()
    this.refreshResults()
  }

  /**
   * Switch to AI mode
   * Shows AI coming soon message
   */
  switchToAiMode() {
    this.modeValue = "ai"
    this.updatePlaceholder()
    this.refreshResults()
  }

  /**
   * Refresh the results based on current mode
   * Triggers a form submission to reload results with the mode filter
   */
  refreshResults() {
    // Reset selection when results change
    this.selectedIndex = -1

    if (this.hasSearchInputTarget) {
      const form = this.searchInputTarget.closest("form")
      if (form) {
        // Add mode as a hidden input or update existing one
        let modeInput = form.querySelector('input[name="mode"]')
        if (!modeInput) {
          modeInput = document.createElement("input")
          modeInput.type = "hidden"
          modeInput.name = "mode"
          form.appendChild(modeInput)
        }
        modeInput.value = this.modeValue

        // Submit the form to reload results
        form.requestSubmit()
      }
    }
  }

  /**
   * Update search input placeholder based on current mode
   */
  updatePlaceholder() {
    if (!this.hasSearchInputTarget) return

    const placeholders = {
      "pages": "Search for recipes, ingredients, orders...",
      "actions": "Search quick actions...",
      "ai": "Ask ✨ AI"
    }

    this.searchInputTarget.placeholder = placeholders[this.modeValue] || placeholders["pages"]
  }

  /**
   * Update footer shortcuts based on current mode
   */
  updateFooter() {
    if (!this.hasFooterTarget) return

    const footerContainer = this.footerTarget.querySelector('.flex.items-center.justify-center')
    if (!footerContainer) return

    // Clear current content
    footerContainer.innerHTML = ''

    if (this.modeValue === "actions" || this.modeValue === "ai") {
      // Actions or AI mode: show Back and Navigate shortcuts
      footerContainer.innerHTML = `
        <div class="flex items-center gap-2">
          <span class="text-xs text-text-secondary">Back</span>
          <div class="flex items-center gap-1">
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">Esc</kbd>
          </div>
        </div>
        <div class="w-px h-4 bg-filled-2"></div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-text-secondary">Navigate</span>
          <div class="flex items-center gap-1">
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">↑</kbd>
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">↓</kbd>
          </div>
        </div>
      `
    } else {
      // Pages mode: show Navigate, Quick Actions, and AI shortcuts
      footerContainer.innerHTML = `
        <div class="flex items-center gap-2">
          <span class="text-xs text-text-secondary">Navigate</span>
          <div class="flex items-center gap-1">
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">↑</kbd>
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">↓</kbd>
          </div>
        </div>
        <div class="w-px h-4 bg-filled-2"></div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-text-secondary">Quick Actions</span>
          <div class="flex items-center gap-1">
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">⌘</kbd>
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">Enter</kbd>
          </div>
        </div>
        <div class="w-px h-4 bg-filled-2"></div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-text-secondary">✨ AI</span>
          <div class="flex items-center gap-1">
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">⌘</kbd>
            <kbd class="text-xs font-mono text-text-secondary bg-filled-1 border border-filled-2 px-1.5 py-0.5 rounded shadow-sm">I</kbd>
          </div>
        </div>
      `
    }
  }


  /**
   * Focus the search input field
   * Called when modal opens
   */
  focusSearchInput() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus()
    }
  }

  /**
   * Clear the search input field
   * Called when modal closes
   */
  clearSearchInput() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""

      // Optionally trigger a new search to reset results
      // This will show the empty state or initial state
      const form = this.searchInputTarget.closest("form")
      if (form) {
        // Use requestSubmit to trigger form submission with proper events
        form.requestSubmit()
      }
    }
  }

  /**
   * Handle arrow key navigation through results
   * - ArrowDown: Move to next result
   * - ArrowUp: Move to previous result
   * - Enter: Navigate to selected result
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleArrowNavigation(event) {
    // Only handle if modal is open
    if (!this.isOpen) {
      return
    }

    // Only handle arrow keys and Enter when not modifying text in search input
    const isArrowDown = event.key === "ArrowDown"
    const isArrowUp = event.key === "ArrowUp"
    const isEnter = event.key === "Enter"

    if (!isArrowDown && !isArrowUp && !isEnter) {
      return
    }

    // Get all result items
    const resultItems = this.resultItemTargets

    if (resultItems.length === 0) {
      return
    }

    // Handle Enter key - navigate to selected result
    if (isEnter && this.selectedIndex >= 0 && this.selectedIndex < resultItems.length) {
      event.preventDefault()
      resultItems[this.selectedIndex].click()
      return
    }

    // Handle arrow keys
    if (isArrowDown || isArrowUp) {
      event.preventDefault()

      // Remove highlight from current selection
      if (this.selectedIndex >= 0 && this.selectedIndex < resultItems.length) {
        this.removeHighlight(resultItems[this.selectedIndex])
      }

      // Update selected index
      if (isArrowDown) {
        this.selectedIndex = Math.min(this.selectedIndex + 1, resultItems.length - 1)
      } else if (isArrowUp) {
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      }

      // Add highlight to new selection
      const selectedItem = resultItems[this.selectedIndex]
      this.addHighlight(selectedItem)

      // Scroll selected item into view
      this.scrollIntoView(selectedItem)
    }
  }

  /**
   * Add visual highlight to a result item
   * @param {HTMLElement} item - The result item to highlight
   */
  addHighlight(item) {
    // Only add background highlight, rounded-md is already applied permanently
    item.classList.add("bg-filled-1")
    item.setAttribute("data-selected", "true")
  }

  /**
   * Remove visual highlight from a result item
   * @param {HTMLElement} item - The result item to unhighlight
   */
  removeHighlight(item) {
    // Only remove background highlight, keep rounded-md
    item.classList.remove("bg-filled-1")
    item.removeAttribute("data-selected")
  }

  /**
   * Scroll a result item into view if it's outside the visible area
   * @param {HTMLElement} item - The result item to scroll into view
   */
  scrollIntoView(item) {
    if (this.hasResultsContainerTarget) {
      const container = this.resultsContainerTarget
      const itemRect = item.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()

      // Check if item is above visible area
      if (itemRect.top < containerRect.top) {
        item.scrollIntoView({ block: "nearest", behavior: "smooth" })
      }
      // Check if item is below visible area
      else if (itemRect.bottom > containerRect.bottom) {
        item.scrollIntoView({ block: "nearest", behavior: "smooth" })
      }
    }
  }
}
